import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import taskingLogo from '@/assets/tasking-logo.png';

const navLinks = [
  { label: 'Features', href: '#features', isRoute: false },
  { label: 'Security', href: '/security', isRoute: true },
  { label: 'Skills', href: '/skills', isRoute: true },
  { label: 'Compare', href: '/compare', isRoute: true },
  { label: 'Pricing', href: '/pricing', isRoute: true },
  { label: 'Docs', href: '/docs', isRoute: true },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-b border-border/50">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={taskingLogo} alt="tasking.tech" className="h-10 w-10 md:h-16 md:w-16 rounded-lg" />
          <span className="navbar-brand-text">
            tasking<span className="text-primary">.tech</span>
            <span
              className="text-muted-foreground ml-1 relative"
              style={{ top: '-0.5em', fontSize: '1rem' }}
            >
              ®
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) =>
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className="relative text-xl text-muted-foreground transition-colors hover:text-foreground after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:w-full after:scale-x-0 after:bg-orange-500 after:shadow-[0_0_8px_rgba(249,115,22,0.6)] after:transition-transform after:duration-300 after:origin-center hover:after:scale-x-100"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="relative text-xl text-muted-foreground transition-colors hover:text-foreground after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:w-full after:scale-x-0 after:bg-orange-500 after:shadow-[0_0_8px_rgba(249,115,22,0.6)] after:transition-transform after:duration-300 after:origin-center hover:after:scale-x-100"
              >
                {link.label}
              </a>
            ),
          )}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle />
          <Link
            to="/login"
            className="rounded-lg border border-border bg-secondary px-7 py-3 text-xl font-semibold text-foreground transition-colors hover:border-primary/30"
          >
            Log in
          </Link>
          <Link
            to="/pricing"
            className="rounded-lg bg-primary px-7 py-3 text-xl font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Get Started
          </Link>
        </div>

        <button
          className="text-muted-foreground md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-border bg-background md:hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm text-muted-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-sm text-muted-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </a>
                ),
              )}
              <div className="flex flex-col gap-2 mt-4">
                <Link
                  to="/login"
                  className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30 text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Log in
                </Link>
                <Link
                  to="/pricing"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Get Started
                </Link>
              </div>
              <div className="mt-2">
                <ThemeToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
